import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import { VcareConnectorQueryPaths } from 'shared/providers/connector/domain/queries/vcare-connector-queries';

import { Connector } from 'features/connectors/domain/connector';
import ConnectorMonitoringFilter from 'features/connectors/domain/connector-monitoring-filter';
import { CreateConnectorCommand } from 'features/connectors/domain/create-connector-command';
import { ReadConnectorsQuery } from 'features/connectors/domain/read-connectors-query';
import { ReadConnectorsResponse } from 'features/connectors/domain/read-connectors-response';
import ValidateSingleVcareConnectorCommand from 'features/connectors/domain/validate-single-vcare-connector-command';
import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';

const connectorBaseUrl = '/api/v1/Connector';
const organisationUnitBaseUrl = '/api/v1/OrganisationUnit';

export const ConnectorsQueryPaths = {
  GetConnectors: 'ReadConnectors',
  UpdateConnector: 'UpdateConnector',
  CreateConnector: 'CreateConnector',
  DeleteConnector: 'DeleteConnector',
  ValidateSingleVcareConnector: 'ValidateSingleVcareConnector',
  GetMonitoringFilters: 'GetMonitoringFilters',
};

const getConnectors = async (axiosInstance: AxiosInstance, query: ReadConnectorsQuery): Promise<ReadConnectorsResponse> => {
  const body = JSON.stringify(query);
  const response = await axiosInstance.post(`${connectorBaseUrl}/${ConnectorsQueryPaths.GetConnectors}`, body);
  return response.data;
};

const updateConnector = async (axiosInstance: AxiosInstance, connector: Connector): Promise<Connector> => {
  const body = JSON.stringify(connector);
  const response = await axiosInstance.put(`${connectorBaseUrl}/${ConnectorsQueryPaths.UpdateConnector}`, body);
  return response.data;
};

const createConnector = async (axiosInstance: AxiosInstance, connector: CreateConnectorCommand): Promise<Connector> => {
  const body = JSON.stringify(connector);
  const response = await axiosInstance.post(`${connectorBaseUrl}/${ConnectorsQueryPaths.CreateConnector}`, body);
  return response.data;
};

export const useGetConnectors = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ConnectorsQueryPaths.GetConnectors],
    mutationFn: (query: ReadConnectorsQuery) => getConnectors(authenticatedInstance, query),
  });
};

export const useUpdateConnector = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ConnectorsQueryPaths.UpdateConnector],
    mutationFn: (connector: Connector) => updateConnector(authenticatedInstance, connector),
  });
};

export const useCreateConnector = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ConnectorsQueryPaths.CreateConnector],
    mutationFn: (connector: CreateConnectorCommand) => createConnector(authenticatedInstance, connector),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VcareConnectorQueryPaths.ConnectorExists] });
    },
  });
};

const deleteConnector = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${connectorBaseUrl}/${ConnectorsQueryPaths.DeleteConnector}/${id}`);
};

export const useDeleteConnector = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ConnectorsQueryPaths.DeleteConnector],
    mutationFn: (id: string) => deleteConnector(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VcareConnectorQueryPaths.ConnectorExists] });
    },
  });
};

const getLocations = async (axiosInstance: AxiosInstance): Promise<OrganisationUnit[]> => {
  const response = await axiosInstance.get(`${organisationUnitBaseUrl}/ReadLocations`);
  return response.data;
};

export const useGetLocations = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: ['GetLocations'],
    queryFn: () => getLocations(authenticatedInstance),
  });
};

export const validateSingleVcareConnector = async (
  axiosInstance: AxiosInstance,
  command: ValidateSingleVcareConnectorCommand,
): Promise<void> => {
  await axiosInstance.post(`${connectorBaseUrl}/${ConnectorsQueryPaths.ValidateSingleVcareConnector}`, command);
};

export const useValidateSingleVcareConnector = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ConnectorsQueryPaths.ValidateSingleVcareConnector],
    mutationFn: (command: ValidateSingleVcareConnectorCommand) => validateSingleVcareConnector(authenticatedInstance, command),
  });
};

const getMonitorFilters = async (axios: AxiosInstance, signal?: AbortSignal): Promise<ConnectorMonitoringFilter[]> => {
  const response = await axios.get(`${connectorBaseUrl}/${ConnectorsQueryPaths.GetMonitoringFilters}`, { signal });
  return response.data;
};

export const useGetMonitorFilters = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ConnectorsQueryPaths.GetMonitoringFilters],
    queryFn: ({ signal }) => getMonitorFilters(authenticatedInstance, signal),
  });
};
